import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack,
    TextField,
    InputAdornment,
    Typography,
    Avatar,
    Box,
    alpha,
    IconButton,
} from '@mui/material';
import {
    LocalTaxi as TaxiIcon,
    Groups as GroupsIcon,
    Close as CloseIcon,
} from '@mui/icons-material';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

const schema = yup.object({
    taxtipnom: yup
        .string()
        .trim()
        .required('El nombre es obligatorio')
        .max(150, 'Máximo 150 caracteres'),

    taxtipcan: yup
        .number()
        .typeError('La cantidad debe ser un número')
        .integer('La cantidad debe ser un número entero')
        .min(1, 'La cantidad debe ser mayor a 0')
        .max(99, 'La cantidad no puede ser mayor a 99')
        .required('La cantidad por grupo es obligatoria'),

    taxtipdes: yup
        .string()
        .trim()
        .nullable()
        .max(500, 'Máximo 500 caracteres'),
});

const INITIAL_VALUES = {
    taxtipnom: '',
    taxtipcan: 3,
    taxtipdes: '',
};

const TaxiServicesTypeForm = ({
    open,
    onClose,
    onSubmit,
    defaultValues = {},
    loading = false,
}) => {
    const isEditing = Boolean(defaultValues?.taxtipid);

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isSubmitting },
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: INITIAL_VALUES,
    });

    useEffect(() => {
        if (!open) return;

        reset({
            taxtipnom: defaultValues?.taxtipnom ?? '',
            taxtipcan: Number(defaultValues?.taxtipcan ?? 3),
            taxtipdes: defaultValues?.taxtipdes ?? '',
        });
    }, [open, defaultValues, reset]);

    const submitting = isSubmitting || loading;

    return (
        <Dialog
            open={open}
            onClose={submitting ? undefined : onClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    overflow: 'hidden',
                },
            }}
        >
            {/*<DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    fontWeight: 700,
                    pb: 1,
                }}
            >
                <TaxiIcon color="primary" />
                {isEditing ? 'Editar tipo de servicio taxi' : 'Nuevo tipo de servicio taxi'}
            </DialogTitle>*/}

            <DialogTitle sx={{
                background: 'linear-gradient(135deg, #0F172A 0%, #1E3A8A 55%, #2563EB 100%)',
                color: "white",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
            }}>
                <Box display="flex" alignItems="center" gap={1.5}>
                    <Avatar sx={{ bgcolor: alpha('#fff', 0.15), width: 40, height: 40 }}>
                        <TaxiIcon />
                    </Avatar>
                    <Box>
                        <Typography variant="h6" fontWeight={600}>
                            {isEditing ? 'Editar Grupo de Servicio Taxi' : 'Nuevo Grupo de Servicio Taxi'}
                        </Typography>
                    </Box>
                </Box>
                <IconButton onClick={() => onClose} sx={{ color: "white", "&:hover": { bgcolor: alpha('#fff', 0.1) } }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <form onSubmit={handleSubmit(onSubmit)} noValidate>
                <DialogContent dividers sx={{ bgcolor: '#fafafa' }}>
                    <Stack spacing={2.5}>
                        <TextField
                            label="Nombre"
                            fullWidth
                            autoFocus
                            disabled={submitting}
                            {...register('taxtipnom')}
                            error={Boolean(errors.taxtipnom)}
                            helperText={errors.taxtipnom?.message}
                            placeholder="Ej: Taxi compartido"
                        />

                        <TextField
                            label="Cantidad por grupo"
                            type="number"
                            fullWidth
                            disabled={submitting}
                            {...register('taxtipcan')}
                            error={Boolean(errors.taxtipcan)}
                            helperText={errors.taxtipcan?.message || 'Número máximo de pasajeros por taxi/grupo.'}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <GroupsIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                            inputProps={{
                                min: 1,
                                max: 99,
                                step: 1,
                            }}
                        />

                        <TextField
                            label="Descripción"
                            fullWidth
                            multiline
                            minRows={3}
                            disabled={submitting}
                            {...register('taxtipdes')}
                            error={Boolean(errors.taxtipdes)}
                            helperText={errors.taxtipdes?.message}
                            placeholder="Descripción general del tipo de servicio taxi"
                        />
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ p: 2.5, bgcolor: '#fafafa' }}>
                    <Button
                        onClick={onClose}
                        color="inherit"
                        variant="outlined"
                        disabled={submitting}
                    >
                        Cancelar
                    </Button>

                    <Button
                        type="submit"
                        variant="contained"
                        disabled={submitting}
                        disableElevation
                    >
                        {submitting
                            ? 'Guardando...'
                            : isEditing
                                ? 'Actualizar'
                                : 'Guardar'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default TaxiServicesTypeForm;